/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { isFormulaEmpty, JeVariationStatus, JeVariationType } from '@gh/builder-shared-data';
import {
  conditionalCellRenderer,
  createDefaultGridModel,
  GREEN_MARKER_COLOR,
  notEmptyCellRenderer,
  rendererColorCircle,
  rendererIconByName,
  ROW_DIRTY_CLASS,
  ROW_DIVIDER_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { NumberTypes, State, TextTypes, whenDirty } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import {
  buildEditableCheckboxColumn,
  buildEditableNumberColumn,
  buildEditableSelectColumn,
  buildEditableTextColumn,
  buildGroupTitleColumn,
  buildNumberColumn,
  EditableCellOptionSet,
  gridCollectionComparator,
  GridModel,
  GridOptionsExt,
  gridValueCollectionComparator,
  LayoutBasedGridDefinitionWithModel,
} from '@gh/core-ui-grid';
import { mutableSort, toString } from '@gh/core-util';
import { ColDef } from 'ag-grid-community';
import { constant, get, isEmpty, negate } from 'lodash';
import {
  getJeItemId,
  jeCuttingListValueComparator,
  jeFormulaValueComparator,
  JeItem,
  jeItemFieldState,
  JeItemType,
  jeVariationComparator,
  validateJeItemField,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  JeDetailsState,
  queryJeComponent,
  queryJeCostCenterOrders,
  queryJeEditable,
  queryJeUomRefItem,
} from '../je-details.state';

const quoteVariationRenderer = rendererIconByName('error', '#ff9e21');
const jobVariationRenderer = rendererIconByName('warning-var', '#ff9e21');
const jobVariationApprovedRenderer = rendererIconByName('done', '#666666');

const ROW_SALES_TAX_CLASS = 'row--sales-tax';

const ITEM_TYPE_ORDER = {
  [JeItemType.Item]: 0,
  [JeItemType.Component]: 0,
  [JeItemType.Divider]: 1,
  [JeItemType.SalesTax]: 2,
};

const itemTypeComparator = (left: JeItem, right: JeItem) => ITEM_TYPE_ORDER[left.type] - ITEM_TYPE_ORDER[right.type];

@Injectable()
export class JeDetailsCcItemGridDefinition extends LayoutBasedGridDefinitionWithModel {
  constructor(private state: State<JeDetailsState>,
              private commands: JeCommandService,
              private translations: TranslationService) {
    super();
  }

  buildGridModel(): GridModel {
    return createDefaultGridModel<JeItem>({
      editable: this.state.select(queryJeEditable),
      getId: getJeItemId,
      validate: validateJeItemField,
      getCellDef: jeItemFieldState,
      command: (entity, name, value) => this.commands.costCenterItemSetField(getJeItemId(entity), name, value),
    });
  }

  buildGridOptions(): GridOptionsExt {
    return {
      rowSelection: 'single',
      rowClassRules: {
        [ROW_DIRTY_CLASS]: ({ data }: { data: JeItem }) => whenDirty(data),
        [ROW_SALES_TAX_CLASS]: ({ data }: { data: JeItem }) => data.type === JeItemType.SalesTax,
        [ROW_DIVIDER_CLASS]: ({ data }: { data: JeItem }) => data.type === JeItemType.Divider,
      },
      postSort: (rowNodes) => {
        mutableSort(rowNodes, (leftNode, rightNode) => itemTypeComparator(leftNode.data, rightNode.data));
      },
    };
  }

  buildColumnDefs(): Hash<ColDef> {
    return {
      cost: buildEditableNumberColumn(NumberTypes.SignedPrice, this.model.getCellAccessor('cost')),
      extension: {
        ...buildNumberColumn(NumberTypes.SignedPrice),
      },
      quantity: buildEditableNumberColumn(NumberTypes.SignedQuantity, this.model.getCellAccessor('quantity')),
      variation: {
        field: 'variationStatus',
        comparator: gridCollectionComparator(jeVariationComparator),
        cellRenderer: ({ data: { variationStatus, variationType } }) => {
          if (variationStatus === JeVariationStatus.Confirmed &&
            (variationType === JeVariationType.Job || variationType === JeVariationType.Quotation)) {
            return jobVariationApprovedRenderer;
          } else {
            return variationStatus === JeVariationStatus.Unconfirmed ? {
              [JeVariationType.Quotation]: quoteVariationRenderer,
              [JeVariationType.Job]      : jobVariationRenderer,
            }[variationType] : '';
          }},
      },
      code: {
        ...buildGroupTitleColumn({
          isGrouped: (data: JeItem) => data.type === JeItemType.SalesTax,
          getGroupKey: constant(true),
        }),
        cellClassRules: {
          'warn-color': ({ data }: { data: JeItem }) => data.type === JeItemType.SalesTax,
        },
      },
      description: buildEditableTextColumn(TextTypes.Text, this.model.getCellAccessor('description')),
      uomCode: {
        valueGetter: ({ data }: { data: any }) =>
          <string>get(this.state.project(queryJeUomRefItem, data.uomId), 'code', ''),
      },
      order: buildEditableSelectColumn(this.buildOrderItemSet(), this.model.getCellAccessor('order'), {
        hasAlternativeText: ({ data: { costCenterId } }) =>
          isEmpty(this.state.project(queryJeCostCenterOrders, costCenterId)),
        getAlternativeText: constant(this.translations.get('lbl_no_orders')),
        align: 'left',
        clearable: false,
      }),
      isSalesTax: buildEditableCheckboxColumn(this.model.getCellAccessor('isSalesTax')),
      isLocked: buildEditableCheckboxColumn(this.model.getCellAccessor('isLocked')),
      isComponent: {
        field: 'jobComponentId',
        cellRenderer: truthyCellRenderer(rendererColorCircle(GREEN_MARKER_COLOR)),
      },
      isCut: {
        field: 'cuttingList',
        comparator: gridValueCollectionComparator(jeCuttingListValueComparator),
        cellRenderer: notEmptyCellRenderer(rendererColorCircle(GREEN_MARKER_COLOR)),
        valueGetter: this.itemInheritedValueGetter('cuttingList'),
      },
      isFormula: {
        field: 'formula',
        comparator: gridValueCollectionComparator(jeFormulaValueComparator),
        cellRenderer: conditionalCellRenderer(negate(isFormulaEmpty), rendererColorCircle(GREEN_MARKER_COLOR)),
        valueGetter: this.itemInheritedValueGetter('formula'),
      },
    };
  }

  private buildOrderItemSet(): EditableCellOptionSet {
    return {
      hasOptions: ({ data: { costCenterId } }) => !isEmpty(this.state.project(queryJeCostCenterOrders, costCenterId)),
      getOptions: ({ data: { costCenterId } }) => this.state.project(queryJeCostCenterOrders, costCenterId)
        .map((order) => ({ id: order, name: toString(order.orderNumber) })),
      format: (_, order) => order ? toString(order.orderNumber) : '',
      equalsWith: (_, value, selected) => selected && value.orderId === selected.orderId || false,
    };
  }

  /**
   * Value of some cells of component-based item can be inherited from the corresponding component
   */
  private itemInheritedValueGetter(field: string): any {
    return ({ data }: { data: JeItem }) => (data.type === JeItemType.Component
      ? this.state.project(queryJeComponent, data.jobComponentId)! // tslint:disable-line:no-non-null-assertion
      : data)[field];
  }
}
